import { cn } from '@/lib/utils';
import defaultTopicIconUrl from '@/assets/icon-topic.svg';
import { useSmartImage } from '@/hooks/useSmartImage';

export interface TopicCardProps {
  /** Topic name to display */
  name: string;
  /** CMS-managed background color (hex) */
  color: string | null;
  /** Optional custom SVG icon (raw SVG string from CMS) */
  iconSvg?: string | null;
  /** Click handler for filtering */
  onClick?: () => void;
  /** Whether this topic is currently active/selected */
  isActive?: boolean;
  /** Additional className */
  className?: string;
}

function resolveIconUrl(iconValue?: string | null) {
  const raw = iconValue?.trim();
  if (!raw) return defaultTopicIconUrl;

  // Back-compat: previously stored as raw SVG markup.
  if (raw.startsWith('<svg')) {
    return `data:image/svg+xml;utf8,${encodeURIComponent(raw)}`;
  }

  // New behavior: store a URL to an uploaded SVG in storage.
  return raw;
}

/**
 * component-card-topic
 * 
 * Large pill-shaped topic navigation card using CMS-managed colors.
 * 
 * Tokens applied:
 * - Shape: rounded-full (pill)
 * - Padding: px-6 py-4
 * - Layout: inline-flex items-center gap-3
 * - Icon: 24x24, white fill, flex-shrink-0
 * - Typography: text-lg font-semibold text-white
 * - Surface: CMS-managed topic color (tag_color column)
 * - Hover: scale-[1.02] transition
 */
export function TopicCard({
  name,
  color,
  iconSvg,
  onClick,
  isActive = false,
  className = '',
}: TopicCardProps) {
  const bgColor = color || '#6E6E73'; // Fallback to coal if no color set
  const iconUrl = resolveIconUrl(iconSvg);
  const { ref, inView } = useSmartImage();

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={cn(
        // Responsive sizing: smaller on mobile, larger on desktop
        'inline-flex items-center gap-2 md:gap-3 px-4 py-2.5 md:px-6 md:py-4 rounded-full transition-transform duration-200',
        'md:hover:scale-[1.02] active:scale-[0.98]',
        isActive && 'ring-2 ring-offset-2 ring-carbon',
        className
      )}
      style={{ backgroundColor: bgColor }}
    >
      {/* Icon (SVG file URL or inline SVG). Filter forces a white icon on colored pills. */}
      {inView && (
        <img
          src={iconUrl}
          alt={`${name} topic icon`}
          loading="lazy"
          decoding="async"
          width={24}
          height={24}
          className="flex-shrink-0 w-5 h-5 md:w-6 md:h-6 object-contain"
          style={{ filter: 'brightness(0) invert(1)' }}
        />
      )}
      
      {/* Topic Name - responsive typography */}
      <span className="text-sm md:text-lg font-semibold text-white whitespace-nowrap">
        {name}
      </span>
    </button>
  );
}

export default TopicCard;
